import { ArrowFloorButton, FloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ChevronLeftIcon, ChevronRightIcon, CurrentLocationIcon, PaperIcon } from '@/components/icons';
import { FLOOR_LIST_CONFIG } from '@/constants/floor-guide';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useFloorManagement } from '@/hooks/floor-guide';
import { useConfigStore } from '@/stores';
import { generateAllFloorsGuideWithTTS, generateFloorName, generateFloorNameWithTTS, getFloorLocalizedName, getFloorMapName } from '@/utils/floor-guide';
import clsx from 'clsx';
import type { FloorInfo } from '@/services/dto';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';
import styles from './styles.module.scss';
type Props = {
    floors?: FloorInfo[];
    selectedFloorName?: string;
    isShowAllFloorGuide: boolean;
    onSelectFloor?: (floor: FloorInfo) => void;
    setIsShowAllFloorGuide: (isShow: boolean) => void;
    onShowMyLocation: () => void;
    onShowPoiList?: () => void;
};

export const HorizontalFloorList = ({ floors, selectedFloorName, onSelectFloor, isShowAllFloorGuide, onShowMyLocation, onShowPoiList }: Props) => {
    const { t, i18n } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { speak } = useAccessibilityTTS();
    const isFullscreen = screenMode === 'fullscreen';
    const buttonText = t('common.button.tts.button');
    const inactiveButtonText = t('common.button.tts.inactive_button');

    const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);
    const speakCleanupRef = useRef<(() => void) | null>(null);

    const cancelPendingSpeak = useCallback(() => {
        speakCleanupRef.current?.();
        speakCleanupRef.current = null;
    }, []);

    const scheduleSpeak = useCallback(
        (text: string) => {
            cancelPendingSpeak();
            speakCleanupRef.current = speak(text) ?? null;
        },
        [cancelPendingSpeak, speak]
    );

    const handleFloorActionMouseDown = () => {
        setLastInteractionWasMouse(true);
    };

    const handleMyLocationFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.current_location')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    const handleMyLocationBlur = () => {
        cancelPendingSpeak();
    };

    const handleViewStoreListFocus = () => {
        if (!lastInteractionWasMouse) {
            scheduleSpeak(`${t('floor_guide.button.view_store_list')}, ${buttonText}`);
        }
        setLastInteractionWasMouse(false);
    };

    // State to track current visible index of Swiper
    const [currentActiveIndex, setCurrentActiveIndex] = useState(0);

    const swiperContainerRef = useRef<HTMLDivElement>(null);

    const slidesPerView = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.SLIDES_PER_VIEW : FLOOR_LIST_CONFIG.NORMAL.SLIDES_PER_VIEW;
    const amountToShow = isFullscreen ? FLOOR_LIST_CONFIG.FULLSCREEN.AMOUNT_TO_SHOW : FLOOR_LIST_CONFIG.NORMAL.AMOUNT_TO_SHOW;

    const { handleScrollUp, handleScrollDown, shouldScroll, swiperRef, handleDisableNext, handleDisablePrev, isDisabledPrev, isDisabledNext, floorSlides } =
        useFloorManagement(floors, amountToShow, selectedFloorName, slidesPerView);

    const isSelectedItem = useCallback(
        (floor?: FloorInfo) => {
            if (!floor) {
                return false;
            }
            return getFloorMapName(floor) === selectedFloorName;
        },
        [selectedFloorName]
    );

    // Function to determine if a floor button should have tabIndex based on active slide
    const getFloorTabIndex = useCallback(
        (slideIndex: number) => {
            // Only items in the currently active slide should be focusable
            if (slideIndex !== currentActiveIndex) return -1;

            // Calculate the actual tabIndex: base 4 + item position in slide
            return 8;
        },
        [currentActiveIndex]
    );

    const handleSelectFloor = useCallback(
        (floor?: FloorInfo) => {
            if (!floor) {
                return;
            }
            const floorName = getFloorLocalizedName(floor, i18n.language);
            speak(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
            onSelectFloor?.(floor);
        },
        [onSelectFloor, speak, t, i18n.language]
    );

    const handleFocusFloorBtn = useCallback(
        (floor: FloorInfo, selected: boolean) => {
            if (!floor) return;
            const floorName = getFloorLocalizedName(floor, i18n.language);
            speak(
                generateFloorNameWithTTS(
                    t,
                    t('floor_guide.text_to_speech.floor', {
                        floorName: generateFloorName(floorName),
                    }),
                    selected
                )
            );
        },
        [speak, t]
    );

    // Voice handling for arrow buttons
    const handleScrollUpWithTTS = useCallback(() => {
        if (isDisabledPrev) {
            speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
            handleScrollUp();
        }
    }, [handleScrollUp, isDisabledPrev, speak, t]);

    const handleScrollDownWithTTS = useCallback(() => {
        if (isDisabledNext) {
            speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
            handleScrollDown();
        }
    }, [handleScrollDown, isDisabledNext, speak, t]);

    // Use custom hook to disable wheel events on Swiper container
    useDisableWheelEvent(swiperContainerRef);

    return (
        <section className={styles['floor-wrapper']}>
            <section className={styles['floor__content__wrapper']}>
                <section className={styles['floor__content']}>
                    <section className={styles['floor__list']}>
                        {shouldScroll && (
                            <section className={clsx(styles['floor__scroll-btn'], styles['floor__scroll-btn--prev'])}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={6}
                                    tabIndex={9}
                                    data-interactive
                                    className="swiper-prev"
                                    aria-disabled={isDisabledPrev}
                                    onClick={() => handleScrollUpWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledPrev) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronLeftIcon />
                                </ArrowFloorButton>
                            </section>
                        )}

                        <div ref={swiperContainerRef} className="min-w-0 flex-1 overflow-clip">
                            <Swiper
                                className={clsx(styles['floor__list-container'], 'scrollbar-hide')}
                                mousewheel={false}
                                allowTouchMove={true}
                                simulateTouch={false}
                                direction={'horizontal'}
                                spaceBetween={scaleHeightPixel(24)}
                                navigation={{
                                    prevEl: '.swiper-prev',
                                    nextEl: '.swiper-next',
                                }}
                                slidesPerView={1}
                                loop={false}
                                ref={swiperRef}
                                preventInteractionOnTransition
                                onSlideChange={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                                onInit={(swiper) => {
                                    handleDisablePrev(swiper.isBeginning);
                                    handleDisableNext(swiper.isEnd);
                                    setCurrentActiveIndex(swiper.activeIndex);
                                }}
                            >
                                {floorSlides?.map((floorSlide, slideIndex) => {
                                    return (
                                        <SwiperSlide key={floorSlide.index}>
                                            <div
                                                className="flex w-fit flex-row"
                                                data-region-nav={slideIndex === currentActiveIndex}
                                                data-region-order={slideIndex === currentActiveIndex ? 4 : -1}
                                            >
                                                {floorSlide.slideContent.map((floor) => {
                                                    const tabIndex = getFloorTabIndex(slideIndex);

                                                    return (
                                                        <FloorButton
                                                            data-region-order={5}
                                                            key={floor.id}
                                                            data-tts-scope="floor-button"
                                                            tabIndex={tabIndex}
                                                            data-interactive
                                                            role="radio"
                                                            aria-checked={isSelectedItem(floor)}
                                                            isSelected={isSelectedItem(floor)}
                                                            aria-label={generateFloorNameWithTTS(
                                                                t,
                                                                t('floor_guide.text_to_speech.floor', {
                                                                    floorName: generateFloorName(getFloorLocalizedName(floor, i18n.language)),
                                                                }),
                                                                isSelectedItem(floor)
                                                            )}
                                                            onClick={() => handleSelectFloor(floor)}
                                                            onFocus={() => handleFocusFloorBtn(floor, isSelectedItem(floor))}
                                                        >
                                                            {getFloorLocalizedName(floor, i18n.language) || '-'}
                                                        </FloorButton>
                                                    );
                                                })}
                                            </div>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>

                        {shouldScroll && (
                            <section className={clsx(styles['floor__scroll-btn'], styles['floor__scroll-btn--next'])}>
                                <ArrowFloorButton
                                    data-tts-scope="arrow-floor-button"
                                    data-type="button"
                                    data-region-nav
                                    data-region-order={7}
                                    tabIndex={9}
                                    data-interactive
                                    className="swiper-next"
                                    aria-disabled={isDisabledNext}
                                    onClick={() => handleScrollDownWithTTS()}
                                    onFocus={() => {
                                        if (isDisabledNext) {
                                            speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
                                        } else {
                                            speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
                                        }
                                    }}
                                >
                                    <ChevronRightIcon />
                                </ArrowFloorButton>
                            </section>
                        )}
                    </section>
                    <section className={styles['floor__actions']}>
                        <SecondaryFloorButton
                            data-region-nav
                            tabIndex={10}
                            data-interactive
                            data-region-order={8}
                            data-tts-scope="my-location-button"
                            aria-label={generateAllFloorsGuideWithTTS(t, t('floor_guide.button.all_floors_guide'), isShowAllFloorGuide)}
                            onMouseDown={handleFloorActionMouseDown}
                            onFocus={handleMyLocationFocus}
                            onBlur={handleMyLocationBlur}
                            onClick={onShowMyLocation}
                            isSelected={isShowAllFloorGuide}
                            className="!h-[calc(52*var(--vh-unit))] !w-[calc(172*var(--vw-unit))]"
                        >
                            <CurrentLocationIcon />
                            {t('floor_guide.button.current_location')}
                        </SecondaryFloorButton>
                        <SecondaryFloorButton
                            role="button"
                            data-region-nav
                            data-region-order={9}
                            data-tts-scope="view-store-list-button"
                            tabIndex={10}
                            variant="gray"
                            onMouseDown={handleFloorActionMouseDown}
                            onFocus={handleViewStoreListFocus}
                            onClick={() => onShowPoiList?.()}
                            className="!h-[calc(52*var(--vh-unit))] !w-[calc(172*var(--vw-unit))]"
                        >
                            <span className="flex-shrink-0">
                                <PaperIcon />
                            </span>
                            {t('floor_guide.button.view_store_list')}
                        </SecondaryFloorButton>
                    </section>
                </section>
            </section>
        </section>
    );
};
